import _extends from "@babel/runtime/helpers/extends";
import { getMeasurementDimension, getMeasurementMetric } from '@kentik/ui-shared/query/measurements';
import AgentDisplay, { AGENT_PRESET_COMPACT } from 'app/views/synthetics/components/AgentDisplay';
import SynthTest from 'app/views/ude/components/SynthTest';
import $syn from 'app/stores/synthetics/$syn';
import $sites from 'app/stores/site/$sites';
import React from 'react';
import { applicationRenderer, asnRenderer, asPathRenderer, bgpCommunityRenderer, cdnRenderer, cityRenderer, cloudDetailRenderer, connectivityTypeRenderer, countryRenderer, deviceNameRenderer, deviceStatusRenderer, hideRawValueRenderer, inetFamilyRenderer, interfaceRenderer, interfaceOperAdminStatusRenderer, ipRenderer, kappaRenderer, networkBoundaryRenderer, ottProviderRenderer, ottServiceRenderer, ottServiceTypeRenderer, packetSizeRenderer, portRenderer, protocolRenderer, providerRenderer, regionRenderer, routePrefixRenderer, serviceRenderer, sessionStateRenderer, siteMarketRenderer, siteNameRenderer, timestampMsRenderer, valueOrNameRenderer } from 'app/components/dataviews/views/legend/legendRenderers';
import { applicationMetrics, asnMetrics, asPathMetrics, cdnMetrics, cityMetrics, countryMetrics, deviceMetrics, interfaceMetrics, ipMetrics, ottProviderMetrics, ottServiceMetrics, ottServiceTypeMetrics, regionMetrics, serviceMetrics, siteMarketMetrics, siteMetrics } from 'app/components/dataviews/views/legend/legendUtils';
import { all as cloudDetailMetrics } from 'app/views/core/cloud/cloudDetailMetrics';
import $dictionary from 'app/stores/$dictionary';
import { getIsFastData } from '@kentik/ui-shared/query/query';
import { DEVICE_ID_DIMENSIONS, DEVICE_NAME_DIMENSION } from '@kentik/ui-shared/query/deviceDimensions';
import { formatMetricValue } from 'app/views/ude/dataviews/util/unitFormatter';

/** Dimensions that should render agent names instead of IDs */
const AGENT_DIMENSIONS = ['src_agent', 'dst_agent'];

/** Dimensions that should render test names instead of IDs */
const TEST_DIMENSIONS = ['test_id'];

/** Dimensions that should render site names instead of IDs */
const SITE_ID_DIMENSIONS = ['src_agent_site_id', 'dst_agent_site_id'];

/** Format a dimension value to a display string (resolves agent/test/site IDs to names). */
export function formatDimensionValue(dimensionName, value) {
  const strValue = String(value);
  if (AGENT_DIMENSIONS.includes(dimensionName) && strValue) {
    const agent = $syn.agents?.get(strValue);
    return agent?.label || strValue;
  }
  if (TEST_DIMENSIONS.includes(dimensionName) && strValue) {
    const test = $syn.tests?.get(strValue);
    return test?.get('display_name') || test?.get('name') || strValue;
  }
  if (SITE_ID_DIMENSIONS.includes(dimensionName) && strValue) {
    return $sites.collection.get(strValue)?.get('title') || strValue;
  }
  return strValue;
}

/** Measurement+metric pairs that render as InterfaceOperAdminStatusTag */
const INTERFACE_STATUS_METRICS = {
  '/interfaces/counters': ['admin-status', 'oper-status'],
  'IF-MIB::if': ['if_AdminStatus', 'if_OperStatus']
};

/** Measurement+metric pairs that render as DeviceStatusTag */
const DEVICE_STATUS_METRICS = {
  '/system': ['available', 'status']
};

/** Measurement+metric pairs that render as SessionStateRenderer */
const SESSION_STATE_METRICS = {
  '/protocols/bgp/neighbors': ['session-state']
};

// function wrapRowForRenderer(row)

export function getDimensionRenderer({
  metric,
  query
}) {
  if (metric === 'Port_src' || metric === 'Port_dst') {
    const queryInterval = query.time.lookback > 0 ? query.time.lookback : query.time.end - query.time.start;
    const fastData = getIsFastData(query) ? 'Fast' : 'Auto';
    return portRenderer(false, false, fastData, queryInterval);
  }
  if (countryMetrics.includes(metric)) {
    return countryRenderer();
  }
  if (cityMetrics.includes(metric)) {
    return cityRenderer();
  }
  if (regionMetrics.includes(metric)) {
    return regionRenderer();
  }
  if (interfaceMetrics.includes(metric)) {
    return interfaceRenderer(undefined, {
      showMetadata: false
    });
  }
  if (DEVICE_ID_DIMENSIONS.includes(metric)) {
    return deviceNameRenderer(false, DEVICE_NAME_DIMENSION);
  }
  if (deviceMetrics.includes(metric)) {
    return deviceNameRenderer(false, metric);
  }
  if (siteMetrics.includes(metric)) {
    return siteNameRenderer();
  }
  if (siteMarketMetrics.includes(metric)) {
    return siteMarketRenderer();
  }
  if (metric === 'ktsubtype__kappa__STR08' || metric === 'ktsubtype__kappa__STR14') {
    return kappaRenderer();
  }
  if (metric === 'inet_family') {
    return inetFamilyRenderer();
  }
  if (metric === 'sampledpktsize') {
    return packetSizeRenderer();
  }
  if (metric === 'Proto' || metric === 'protocol') {
    return protocolRenderer();
  }
  if (metric === 'src_route_prefix_len' || metric === 'dst_route_prefix_len') {
    return routePrefixRenderer();
  }
  if (metric === 'i_src_network_bndry_name' || metric === 'i_dst_network_bndry_name' || metric === 'i_ult_exit_network_bndry_name') {
    return networkBoundaryRenderer();
  }
  if (metric === 'src_bgp_community' || metric === 'dst_bgp_community' || metric === 'src_bgp_ext_community' || metric === 'dst_bgp_ext_community' || metric === 'src_bgp_large_community' || metric === 'dst_bgp_large_community') {
    return bgpCommunityRenderer();
  }
  if (metric === 'i_src_connect_type_name' || metric === 'i_dst_connect_type_name' || metric === 'i_ult_exit_connect_type_name') {
    return connectivityTypeRenderer();
  }
  if (metric === 'i_src_provider_classification' || metric === 'i_dst_provider_classification' || metric === 'i_ult_provider_classification') {
    return providerRenderer();
  }
  if (asnMetrics.includes(metric)) {
    return asnRenderer();
  }
  if (asPathMetrics.includes(metric)) {
    return asPathRenderer();
  }
  if (serviceMetrics.includes(metric)) {
    return serviceRenderer();
  }
  if (ipMetrics.includes(metric)) {
    return ipRenderer();
  }
  if (cdnMetrics.includes(metric)) {
    return cdnRenderer();
  }
  if (ottProviderMetrics.includes(metric)) {
    return ottProviderRenderer();
  }
  if (ottServiceMetrics.includes(metric)) {
    return ottServiceRenderer();
  }
  if (ottServiceTypeMetrics.includes(metric)) {
    return ottServiceTypeRenderer();
  }
  if (applicationMetrics.includes(metric)) {
    return applicationRenderer();
  }
  if (cloudDetailMetrics.includes(metric)) {
    return cloudDetailRenderer();
  }
  if ($dictionary.get('hideRawValueDimensions', []).includes(metric)) {
    return hideRawValueRenderer();
  }
  const metricRenderers = $dictionary.get('metricRenderers');
  if (metricRenderers?.[metric] === 'timestamp-ms') {
    return ({
      value
    }) => timestampMsRenderer({
      value: parseInt(String(value), 10)
    });
  }
  if (metric) {
    return valueOrNameRenderer();
  }
  return null;
}
function queryResultModelWrapper(row) {
  return {
    get: key => row.dimensions[key] ?? null,
    has: key => key in row.dimensions,
    get isOverlay() {
      return false;
    }
  };
}
export function rawEventRowModelWrapper(row) {
  return {
    get: key => row[key] ?? null,
    has: key => key in row,
    get isOverlay() {
      return false;
    }
  };
}
function getEnumValues(measurement, dimension) {
  const dimValues = getMeasurementDimension(measurement, dimension)?.Values;
  const metricValues = getMeasurementMetric(measurement, dimension)?.Values;
  const values = dimValues ?? metricValues;
  return values && Object.keys(values).length > 0 ? values : undefined;
}
function enumValueRenderer(name, numValue, enumValues, measurement) {
  const metricMeasurement = measurement.startsWith('/metrics') ? measurement.slice(8) : measurement;
  if (INTERFACE_STATUS_METRICS[metricMeasurement]?.includes(name)) {
    return interfaceOperAdminStatusRenderer(enumValues)({
      value: numValue
    });
  }
  if (DEVICE_STATUS_METRICS[metricMeasurement]?.includes(name)) {
    return deviceStatusRenderer(enumValues)({
      value: numValue
    });
  }
  if (SESSION_STATE_METRICS[metricMeasurement]?.includes(name)) {
    return sessionStateRenderer(enumValues)({
      value: numValue
    });
  }
  const label = enumValues[String(numValue)] ?? enumValues[numValue];
  if (label) {
    return label;
  }
  return null;
}
function metricEnumValueRenderer(metric, value, values, measurement) {
  if (!measurement || value == null || value === '' || !values) {
    return null;
  }
  const numValue = Number(value);
  if (Number.isNaN(numValue)) {
    return null;
  }
  return enumValueRenderer(metric, numValue, values, measurement);
}
function dimensionEnumValueRenderer(measurement, dimension, value, queryMeasurement) {
  if (!measurement || value == null || value === '') {
    return null;
  }
  const enumValues = getEnumValues(measurement, dimension);
  if (!enumValues) {
    return null;
  }
  const numValue = Number(value);
  if (!Number.isNaN(numValue)) {
    return enumValueRenderer(dimension, numValue, enumValues, queryMeasurement);
  }
  const strValue = String(value).toLowerCase();
  const numericKey = Object.keys(enumValues).find(key => enumValues[key].toLowerCase() === strValue);
  if (numericKey != null) {
    return enumValueRenderer(dimension, Number(numericKey), enumValues, queryMeasurement);
  }
  return null;
}
export function renderDimensionValue(dimension, row, query, measurement) {
  const value = row.dimensions[dimension];
  const enumResult = dimensionEnumValueRenderer(measurement, dimension, value, query.measurement);
  if (enumResult) {
    return enumResult;
  }
  if (query.measurement.includes('/synthetics')) {
    if (AGENT_DIMENSIONS.includes(dimension)) {
      return /*#__PURE__*/React.createElement(AgentDisplay, _extends({
        agentId: value
      }, AGENT_PRESET_COMPACT, {
        withPopover: true,
        withDetails: true
      }));
    }
    if (TEST_DIMENSIONS.includes(dimension)) {
      return /*#__PURE__*/React.createElement(SynthTest, {
        testId: value,
        small: true
      });
    }
    if (SITE_ID_DIMENSIONS.includes(dimension)) {
      return siteNameRenderer({
        matchBy: 'id'
      })({
        value,
        model: queryResultModelWrapper(row)
      });
    }
  }
  if (query.measurement.includes('/traffic') || query.measurement.includes('/metrics') || query.measurement.includes('/events')) {
    const renderer = getDimensionRenderer({
      metric: dimension,
      query
    });
    return renderer ? renderer({
      value,
      model: queryResultModelWrapper(row),
      name: dimension
    }) : value;
  }
  return value;
}
export function renderMetricValue(metric, value, options = {}) {
  const {
    metricInfo,
    measurement,
    forcePrefix
  } = options;
  if (value == null) {
    return '-';
  }
  const values = metricInfo?.Values;
  const enumResult = metricEnumValueRenderer(metric, value, values, measurement);
  if (enumResult != null) {
    return enumResult;
  }
  return formatMetricValue(value, metricInfo?.Unit, {
    values: metricInfo?.Values,
    forcePrefix
  }).displayFull;
}